// Story 1.28 — TanStack Query hook for
// GET /api/v1/admin/portfolio-applications.
//
// Backs the AdminPortfolioApplicationsPage queue list. Status defaults
// to "pending" (matches the server's default). The response is already
// pre-ordered by (portfolio_id, submitted_at ASC) so the page can group
// rows client-side with a single reduce.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type AdminPortfolioApplicationStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "withdrawn"
  | "any";

export type AdminPortfolioApplicationsFilters = {
  status: AdminPortfolioApplicationStatus;
  portfolio_id?: string;
  submitted_since?: string;
  submitted_until?: string;
  display_name_search?: string;
  page: number;
  page_size: number;
};

export const DEFAULT_PORTFOLIO_APPLICATIONS_FILTERS: AdminPortfolioApplicationsFilters = {
  status: "pending",
  page: 1,
  page_size: 50,
};

export type AdminPortfolioApplicationsListRow = {
  portfolio_application_id: string;
  portfolio_id: string;
  portfolio_title: string;
  user_id: string;
  creator_display_name: string | null;
  creator_handle: string | null;
  effective_hello_text: string | null;
  status: "pending" | "approved" | "rejected" | "withdrawn";
  submitted_at: string;
};

export type AdminPortfolioApplicationsListResponse = {
  data: AdminPortfolioApplicationsListRow[];
  page: number;
  page_size: number;
  has_next: boolean;
  total_pending: number | null;
};

function buildQueryString(filters: AdminPortfolioApplicationsFilters): string {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.portfolio_id) params.set("portfolio_id", filters.portfolio_id);
  if (filters.submitted_since)
    params.set("submitted_since", filters.submitted_since);
  if (filters.submitted_until)
    params.set("submitted_until", filters.submitted_until);
  if (filters.display_name_search)
    params.set("display_name_search", filters.display_name_search);
  params.set("page", String(filters.page));
  params.set("page_size", String(filters.page_size));
  return params.toString();
}

async function fetchAdminPortfolioApplications(
  filters: AdminPortfolioApplicationsFilters,
): Promise<AdminPortfolioApplicationsListResponse> {
  const qs = buildQueryString(filters);
  const response = await fetch(
    `/api/v1/admin/portfolio-applications?${qs}`,
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let message = `Admin portfolio-application queue fetch failed (HTTP ${response.status})`;
    try {
      const raw = await response.json();
      if (raw?.error?.message) message = raw.error.message;
    } catch {
      // body not JSON
    }
    throw new Error(message);
  }
  return (await response.json()) as AdminPortfolioApplicationsListResponse;
}

export function useAdminPortfolioApplications(
  filters: AdminPortfolioApplicationsFilters,
) {
  return useQuery<AdminPortfolioApplicationsListResponse, Error>({
    queryKey: ["admin-portfolio-applications", filters],
    queryFn: () => fetchAdminPortfolioApplications(filters),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
